/**
 * Live, client-side mini-game previews. The backend's template registry carries the built HTML5 runtime and every
 * template's bundle (the same files the packager ships), and this module assembles them into one HTML document
 * that the composer shows in a sandboxed `srcdoc` iframe (scripts only, opaque origin). Every edit re-renders the
 * document locally, so owners see and play their mini-game without a server round trip.
 *
 * The document follows the runtime's mount contract 1 (spark-creatives `html5/README.md`): load `runtime.js` and
 * `<template>.js`, then `SparkPlayable.mount(root, {template, size, config, assets, fonts})`. The CTA, the T&Cs
 * link and the tracking events are routed to the parent with `postMessage` instead of opening anything.
 */
import type { BannerKitParams, KitBox, KitQA } from './banner-kit-api';
import type { JsonObject, PlayableParams, PlayableVersionAssets } from './playable-api';

/** The built HTML5 runtime as served with the template registry. */
export interface PlayableRuntime {
  /** Content hash of the bundles. */
  version: string;
  /** The mount contract the bundles implement. */
  contract: number;
  js: string;
  css: string;
  templates: Record<string, { js: string; css: string }>;
}

/** The `postMessage` type every preview document sends to the composer. */
export const PREVIEW_MESSAGE_TYPE = 'spk-preview';

/** Where a Studio preview document (mounted with `preview: true`) is: on its resting loop (a game's attract, a
 *  banner's final frame), playing (a game's reveal, a banner's frame sequence), or ended (a game's end card). */
export type PreviewDocState = 'resting' | 'playing' | 'ended';

const PREVIEW_DOC_STATES: readonly PreviewDocState[] = ['resting', 'playing', 'ended'];

/** A message from a preview document: a press on the CTA or the T&Cs link, a tracking event, or (a Studio preview
 *  document only) its state on mount and on every change. */
export type PreviewMessage =
  | { type: typeof PREVIEW_MESSAGE_TYPE; kind: 'cta' | 'open'; url: string }
  | { type: typeof PREVIEW_MESSAGE_TYPE; kind: 'track'; event: string }
  | { type: typeof PREVIEW_MESSAGE_TYPE; kind: 'state'; state: PreviewDocState };

/** Narrow an unknown `message` event payload to a preview message. Pure. */
export const asPreviewMessage = (data: unknown): PreviewMessage | null => {
  if (typeof data !== 'object' || data === null) return null;
  const d = data as Record<string, unknown>;
  if (d.type !== PREVIEW_MESSAGE_TYPE) return null;
  if ((d.kind === 'cta' || d.kind === 'open') && typeof d.url === 'string') {
    return { type: PREVIEW_MESSAGE_TYPE, kind: d.kind, url: d.url };
  }
  if (d.kind === 'track' && typeof d.event === 'string') {
    return { type: PREVIEW_MESSAGE_TYPE, kind: 'track', event: d.event };
  }
  const state = PREVIEW_DOC_STATES.find((s) => s === d.state);
  if (d.kind === 'state' && state) return { type: PREVIEW_MESSAGE_TYPE, kind: 'state', state };
  return null;
};

/** The `postMessage` type the Studio drives a preview document with (`MountOptions.preview`, `html5/README.md`). */
export const PREVIEW_CONTROL_TYPE = 'spk-preview-control';

/**
 * What the Studio asks a preview document to do:
 *  - `play`: a game goes on from its attract into its own reveal and ends on the end card; a banner plays its frame
 *    sequence from frame 1;
 *  - `rest`: a short in-document cross-fade back to the resting state (a game's attract loop, a banner's final frame);
 *  - `replay`: a cross-fade to a fresh mount, then `play`.
 */
export type PreviewAction = 'play' | 'rest' | 'replay';

/** Sends a control to a preview document (an opaque-origin sandbox, hence `"*"`); a frame that is gone or a runtime
 *  without the control channel just ignores it. */
export const postPreviewControl = (win: Window | null | undefined, action: PreviewAction): void => {
  if (!win) return;
  try {
    win.postMessage({ type: PREVIEW_CONTROL_TYPE, action }, '*');
  } catch {
    // The frame is being torn down: nothing to drive.
  }
};

/** A picker card's demo as the owner moves over it: hovered (or focused) or not, and how often Replay was pressed. */
export interface CardDemoIntent {
  playing: boolean;
  run: number;
}

/**
 * The one control a card's change of intent sends its document (null = none): Replay while hovered replays (never
 * a `play` too, though Replay also hovers the card), entering plays, leaving rests. Pure.
 */
export const previewControlFor = (prev: CardDemoIntent, next: CardDemoIntent): PreviewAction | null => {
  if (next.playing && next.run !== prev.run) return 'replay';
  if (next.playing && !prev.playing) return 'play';
  if (!next.playing && prev.playing) return 'rest';
  return null;
};

const isHttpUrl = (v: unknown): v is string => typeof v === 'string' && /^https?:\/\//i.test(v.trim());

/**
 * The runtime's asset map for a version's art in one format (`html5/README.md` "Asset keys"): the background as
 * `background` (the plate rendered for this format when the template paints one per format, else the single
 * Seedream plate), each sprite sheet under its key (the subject cut-out, `sprites.subject`, is `subject`) and its
 * cells as `<key>.<i>`, the reveal image (the template's `reveal_image_url`, else the subject's key art) as
 * `reveal`, the owner's card back as `card_back`, the brand logo as `logo` and its bare symbol (`brand.logos.symbol`) as
 * `logo_symbol`. Only http(s) URLs are passed. Pure.
 */
export const previewAssets = (
  assets: PlayableVersionAssets | undefined,
  params: PlayableParams,
  format?: string,
): Record<string, string> => {
  const out: Record<string, string> = {};
  const plate = format ? assets?.plates?.[format] : undefined;
  if (isHttpUrl(plate)) out.background = plate;
  else if (isHttpUrl(assets?.background_url)) out.background = assets.background_url;
  Object.entries(assets?.sprites ?? {}).forEach(([key, url]) => {
    if (isHttpUrl(url)) out[key] = url;
  });
  Object.entries(assets?.sliced ?? {}).forEach(([key, cells]) => {
    cells.forEach((url, i) => {
      if (isHttpUrl(url)) out[`${key}.${i}`] = url;
    });
  });
  const reveal = params.config.reveal_image_url;
  if (isHttpUrl(reveal)) out.reveal = reveal;
  else if (isHttpUrl(params.subject.image_url)) out.reveal = params.subject.image_url;
  if (isHttpUrl(params.config.card_back_url)) out.card_back = params.config.card_back_url;
  if (isHttpUrl(params.brand.logo_url)) out.logo = params.brand.logo_url;
  if (isHttpUrl(params.brand.logos?.symbol)) out.logo_symbol = params.brand.logos.symbol;
  return out;
};

/**
 * The runtime's `PlayableConfig` for a mini-game document: the document without its subject and the subject's copy in
 * the ad's language (both are Studio data: the locked words already carry that copy), with the brand (its palette,
 * CTA button style and fonts) as is, the subject's key art offered to the template as `config.subject_image` (the
 * reveal fallback) unless the template sets one, and the ad's language when it has one (the runtime writes the legal
 * line, its labels and the document's `lang` in it). Pure.
 */
export const previewMountConfig = (params: PlayableParams): Record<string, unknown> => {
  const { subject, localized_copy: _localized, language, ...rest } = params;
  const config: JsonObject = { ...params.config };
  if (!('subject_image' in config) && isHttpUrl(subject.image_url)) config.subject_image = subject.image_url;
  const lang = (language ?? '').trim();
  return { ...rest, config, ...(lang ? { language: lang } : {}) };
};

/** The runtime's size for a format: `{w, h}` for `WxH`, else responsive. Pure. */
export const previewMountSize = (format: string): { w: number; h: number } | 'responsive' => {
  const m = /^(\d+)x(\d+)$/.exec(format);
  return m ? { w: Number(m[1]), h: Number(m[2]) } : 'responsive';
};

// A bundle or stylesheet is inlined verbatim, so a closing tag inside it must not end the element early.
const scriptSafe = (s: string): string => s.replace(/<\/(script)/gi, '<\\/$1');
const styleSafe = (s: string): string => s.replace(/<\/(style)/gi, '<\\/$1');
// JSON inside a <script> element: no raw "<" (no closing tag, no comment opener) and no JS line separators.
const jsonForScript = (v: unknown): string =>
  JSON.stringify(v).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');

/**
 * How a picker card plays its demo:
 *  - `idle`: the attract animation only (no hint, no auto-reveal), the card's resting loop; mounted as a controlled
 *    preview, the same document plays through when the card posts `play`;
 *  - `play`: a quick playthrough on hover (no hint, the template's own reveal after a short attract, then the end
 *    card), the second document a card loads over its loop only when the runtime has no control channel.
 * The composer's live panel keeps the owner's own timing.
 */
export type PreviewMode = 'idle' | 'play';

/** How long a hovered card shows the attract before the game reveals itself. */
export const PREVIEW_PLAY_REVEAL_MS = 900;

/**
 * The CTA a picker demo shows: the document's own, unless it is empty or a "Deposit" call (acquisition ads never
 * lead with Deposit, the same rule the config fill applies), then `fallback`. Pure.
 */
export const withDemoCta = (params: PlayableParams, fallback: string): PlayableParams => {
  const cta = (params.copy.cta ?? '').trim();
  if (cta && !/deposit/i.test(cta)) return params;
  return { ...params, copy: { ...params.copy, cta: fallback } };
};

/** The document's params retimed for a card mode (the owner's timing is untouched elsewhere). Pure. */
export const withPreviewMode = (params: PlayableParams, mode: PreviewMode): PlayableParams => ({
  ...params,
  timing: { ...params.timing, hint_ms: 0, auto_reveal_ms: mode === 'idle' ? 0 : PREVIEW_PLAY_REVEAL_MS },
});

/** The runtime's mount options (`html5/README.md` "Mount contract"): which template, at which size, its config
 *  (the ad document), the asset map, how fonts load and (the Studio's picker and library cards only, never a
 *  package) whether the document is a controlled preview. */
export interface PreviewMount {
  template: string;
  size: { w: number; h: number } | 'responsive';
  config: Record<string, unknown>;
  assets: Record<string, string>;
  fonts: 'link' | 'system';
  /** A Studio preview: it rests (no auto-reveal, no 30 s ambient-motion stop) until the parent posts `play`, and
   *  reports its state (see `PreviewAction`, `PreviewDocState`). A runtime without the channel ignores it. */
  preview?: boolean;
}

/** How a preview document is built around its mount (never part of what the runtime is mounted with). */
export interface PreviewDocBuild {
  /** A still: the document answers `prefers-reduced-motion: reduce` whatever the viewer's setting, so the runtime
   *  draws without motion. With `PreviewMount.preview`, the game rests on its attract scene, static (`html5/README.md`
   *  "Preview control": reduced motion rests too); without it the runtime would jump to its end card. */
  still?: boolean;
}

/** How a preview document is mounted. */
export interface PreviewDocOptions extends PreviewDocBuild {
  /** Mount it as a controlled Studio preview (`PreviewMount.preview`): the picker cards and the library cards. */
  preview?: boolean;
}

/** Runs before the runtime: `matchMedia` answers the reduced-motion query as matching (every other query as is). */
const STILL_SHIM =
  '(function(){var Q=/prefers-reduced-motion\\s*:\\s*reduce/i,mm=window.matchMedia;' +
  'function noop(){}' +
  'window.matchMedia=function(q){if(Q.test(String(q)))return{matches:true,media:String(q),onchange:null,' +
  'addListener:noop,removeListener:noop,addEventListener:noop,removeEventListener:noop,' +
  'dispatchEvent:function(){return false}};return mm.call(window,q)};})();';

/**
 * The preview document mounting `mount` with the runtime and the template's bundle, or null when the runtime has no
 * bundle for the template. Pure: the same inputs always give the same document (the iframe reloads only when
 * something changed).
 */
export const buildPreviewDoc = (
  runtime: PlayableRuntime,
  mount: PreviewMount,
  build: PreviewDocBuild = {},
): string | null => {
  const tpl = runtime.templates[mount.template];
  if (!tpl) return null;
  const bridge =
    `(function(){var T=${jsonForScript(PREVIEW_MESSAGE_TYPE)};` +
    'function post(m){try{parent.postMessage(m,"*")}catch(e){}}' +
    'window.__spkCta=function(u){post({type:T,kind:"cta",url:String(u||"")})};' +
    'window.__spkOpen=function(u){post({type:T,kind:"open",url:String(u||"")})};' +
    'window.__spkTrack=function(e){post({type:T,kind:"track",event:String(e||"")})};})();';
  const start =
    'window.addEventListener("load",function(){var sp=window.SparkPlayable;' +
    `if(sp&&sp.mount){sp.mount(document.getElementById("ad"),${jsonForScript(mount)});}});`;
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width,initial-scale=1">' +
    `<style>html,body{margin:0;height:100%;overflow:hidden}#ad{width:100%;height:100%}${styleSafe(runtime.css)}${styleSafe(tpl.css)}</style>` +
    `<script>${scriptSafe(bridge)}</script>` +
    (build.still ? `<script>${STILL_SHIM}</script>` : '') +
    '</head><body><div id="ad"></div>' +
    `<script>${scriptSafe(runtime.js)}</script><script>${scriptSafe(tpl.js)}</script>` +
    `<script>${scriptSafe(start)}</script></body></html>`
  );
};

/**
 * The preview document of a mini-game (or HTML5 banner) in one format, or null when the runtime has no bundle for
 * the template. Pure.
 */
export const buildPlayablePreviewDoc = (
  runtime: PlayableRuntime,
  templateId: string,
  format: string,
  params: PlayableParams,
  assets: PlayableVersionAssets | undefined,
  options: PreviewDocOptions = {},
): string | null =>
  buildPreviewDoc(
    runtime,
    {
      template: templateId,
      size: previewMountSize(format),
      config: previewMountConfig(params),
      assets: previewAssets(assets, params, format),
      fonts: 'link',
      ...(options.preview ? { preview: true } : {}),
    },
    { still: options.still },
  );

/* ---------- an Image Banners kit, animated (the kit HTML5 export, previewed in the browser) ---------- */

/** The internal template the kit HTML5 export animates a size's render with. */
export const KIT_BANNER_TEMPLATE = 'animated-banner';

/** A QA box as the animated banner takes it: a normalised `[l, t, r, b]` inside the render (right and bottom clamped
 *  to 1), or null when it is missing or invalid (mirrors the backend's `kitMountConfig`). Pure. */
export const kitMountBox = (box: KitBox | undefined | null): KitBox | null => {
  if (!Array.isArray(box) || box.length !== 4 || box.some((v) => typeof v !== 'number' || !Number.isFinite(v))) {
    return null;
  }
  const [l, t, r, b] = box;
  if (l < 0 || t < 0 || r > 1.001 || b > 1.001 || r <= l || b <= t) return null;
  return [l, t, Math.min(r, 1), Math.min(b, 1)];
};

/** What one size of an animated kit banner is mounted with. */
export interface KitBannerMountInput {
  /** The kit document the export reads (the version's, with the kit's own brand and legal line). */
  params: Pick<BannerKitParams, 'copy' | 'brand' | 'legal'>;
  /** The size's render QA boxes (the CTA pulse, and where the legal line keeps clear of). */
  boxes?: KitQA['boxes'];
  /** The resolved legal line ('' = none). */
  legalText: string;
  /** The click-through. */
  landingUrl: string;
}

/** The animated banner's mount config for one size of a kit (mirrors the backend's `kitMountConfig`): the CTA box,
 *  the logo and headline boxes to keep the legal line off, three loops, the copy, the brand, the resolved legal line
 *  and the click-through. Pure. */
export const kitBannerMountConfig = ({ params, boxes, legalText, landingUrl }: KitBannerMountInput): Record<string, unknown> => {
  const avoid: Record<string, KitBox> = {};
  (['logo', 'headline'] as const).forEach((key) => {
    const box = kitMountBox(boxes?.[key]);
    if (box) avoid[key] = box;
  });
  return {
    template_id: KIT_BANNER_TEMPLATE,
    schema_version: 1,
    config: { cta_box: kitMountBox(boxes?.cta), avoid_boxes: avoid, loops: 3 },
    copy: { headline: params.copy.headline, cta: params.copy.cta },
    offer: {},
    brand: params.brand,
    legal: { enabled: legalText !== '', preset: params.legal.preset, text: legalText },
    landing_url: landingUrl,
  };
};

/** The preview document of one size of a kit animated (the render as `art`), or null when the runtime has no
 *  animated-banner bundle. Pure. */
export const buildKitBannerPreviewDoc = (
  runtime: PlayableRuntime,
  size: string,
  artUrl: string,
  input: KitBannerMountInput,
): string | null => {
  const assets: Record<string, string> = {};
  if (isHttpUrl(artUrl)) assets.art = artUrl;
  if (isHttpUrl(input.params.brand.logo_url)) assets.logo = input.params.brand.logo_url;
  return buildPreviewDoc(runtime, {
    template: KIT_BANNER_TEMPLATE,
    size: previewMountSize(size),
    config: kitBannerMountConfig(input),
    assets,
    fonts: 'link',
  });
};
